import { useState } from 'react';
import { Info, FileText, Mail, ArrowRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useDocumentTitle } from '@/shared/hooks/use-document-title';

interface FAQItem {
  pregunta: string;
  respuesta: string;
}

interface GlosarioItem {
  termino: string;
  definicion: string;
}

const PREGUNTAS_FRECUENTES: FAQItem[] = [
  {
    pregunta: '¿Qué es una EPS?',
    respuesta:
      'Son las instituciones públicas y/o privadas distintas de ESSALUD cuyo único fin es el de prestar servicios de atención para la salud con infraestructura propia o de terceros, conforme a lo estipulado en la normatividad; y supervisadas por la Superintendencia Nacional de Salud -SUSALUD-.',
  },
  {
    pregunta: '¿Qué es SUSALUD?',
    respuesta:
      'La Superintendencia Nacional de Salud -SUSALUD- es la institución encargada de proteger los derechos en salud de quienes han recibido todo tipo de prestaciones de salud, sin importar donde se atienda o su condición de aseguramiento.',
  },
  {
    pregunta: '¿Quiénes tienen derecho a tener EPS?',
    respuesta:
      'Los asegurados regulares y sus derechohabientes; y los asegurados potestativos y sus derechohabientes. De los asegurados regulares: Son los trabajadores activos que laboran bajo relación de dependencia, denominados como asegurados regulares o dependientes. De los asegurados potestativos: Son todas las personas no comprendidas en el inciso i), denominados como asegurados potestativos o independientes. De los derechohabientes: Son derechohabientes el/la cónyuge o el/la concubino(a), los hijos menores de edad y los hijos mayores incapacitados en forma total y permanente para el trabajo, siempre que no sean afiliados obligatorios.',
  },
  {
    pregunta: '¿Es obligatorio pertenecer a una EPS?',
    respuesta:
      'No. El trabajador de una empresa es libre de elegir si permanece en EsSalud o si opta por tomar el plan médico de salud con la EPS.',
  },
  {
    pregunta: '¿Tengo que pagar para ser atendido?',
    respuesta:
      'Las prestaciones de salud están sujetas a copagos (debes pagar una parte de la suma total). Los montos de dichos copagos se encuentran indicados en el plan de salud emitido por la EPS con la que se contrató.',
  },
  {
    pregunta: '¿Qué es una preexistencia?',
    respuesta:
      'Una preexistencia, para la Ley N° 29561, es cualquier condición de alteración del estado de salud diagnosticada por un profesional médico colegiado, conocida por el titular o derechohabiente y no resuelta en el momento previo a la presentación de la declaración jurada de salud.',
  },
  {
    pregunta: '¿Qué prestaciones brindan las EPS?',
    respuesta:
      'Las EPS brindan las siguientes prestaciones: Preventivas y Promocionales: Educación para la salud, Evaluación y control de riesgos. Recuperación de la salud: Atención médica (Emergencias, Ambulatoria y Hospitalización), Medicinas e insumos médicos, Prótesis y aparatos ortopédicos imprescindibles, Servicios de rehabilitación. Facultativamente, las EPS pueden ofrecer las siguientes prestaciones, siempre y cuando estén incluidas en tu plan de salud: Bienestar y Promoción social, Prestaciones Económicas.',
  },
  {
    pregunta: '¿Qué Condiciones de Salud cubren las EPS?',
    respuesta:
      'Las EPS están obligadas cubrir las condiciones de salud especificadas en el Plan de Beneficios del PEAS, regulado en el Decreto Supremo N° 016-2009-SA.',
  },
  {
    pregunta: '¿Qué es el PEAS?',
    respuesta:
      'Es el Plan Esencial de Aseguramiento en Salud -PEAS-, que consiste en la lista priorizada de condiciones asegurables e intervenciones que como mínimo son financiadas a todos los asegurados por las IAFAS, como lo son las EPS.',
  },
  {
    pregunta: '¿Qué son las IAFAS?',
    respuesta:
      'Las Instituciones Administradoras de Fondos de Aseguramiento en Salud -IAFAS- son aquellas entidades o empresas públicas, privadas o mixtas, creadas o por crearse, que reciban, capten y/o gestionen fondos para la cobertura de las atenciones de salud o que oferten cobertura de riesgos de salud, bajo cualquier modalidad.',
  },
  {
    pregunta: '¿Hay límite de edad para afiliarse?',
    respuesta:
      'Para asegurados regulares o dependientes, el límite para estar afiliado a una EPS es hasta los 70 años. Para los asegurados potestativos o independientes, no existe límite de edad para la afiliación de trabajadores.',
  },
  {
    pregunta: '¿Qué pasa si llego al límite de cobertura de mi plan de EPS?',
    respuesta:
      'La EPS, bajo responsabilidad, procederá a coordinar de manera oportuna la transferencia del paciente a un Hospital del Seguro Social de Salud -EsSalud-.',
  },
  {
    pregunta:
      '¿Qué sucede con las prestaciones económicas brindadas por EsSalud si estoy afiliado a una EPS?',
    respuesta:
      'Si eres un asegurado regular o dependiente, mantendrás el derecho de recibir las prestaciones económicas de EsSalud. Si eres un asegurado potestativo o independiente, no tendrás derecho a recibir las prestaciones económicas de EsSalud.',
  },
  {
    pregunta: '¿El afiliado a una EPS mantiene su derecho de atención en EsSalud?',
    respuesta:
      'Sí, en los siguientes 2 casos: El asegurado regular o dependiente es diagnosticado con condiciones no cubiertas por el PEAS (capa compleja). La cobertura de un asegurado regular o dependiente ha llegado al límite establecido en el Plan de Salud que tiene con la EPS.',
  },
  {
    pregunta: '¿Qué es el periodo de carencia?',
    respuesta: 'Es el tiempo durante el cual las coberturas aún no están activas.',
  },
  {
    pregunta: '¿Cuánto debo esperar para recibir la cobertura de mi EPS?',
    respuesta:
      'Para los diagnósticos del PEAS, podrás atenderte siempre que cuentes con 3 meses de aportación consecutiva o 4 no consecutivos a EsSalud, dentro de los 6 meses anteriores al mes en que se inició la inscripción, salvo en los casos de emergencia. Para los diagnósticos no cubiertos por el PEAS, se aplicará un periodo de carencia de 90 días a partir de la fecha de inscripción, salvo en los casos de emergencia.',
  },
  {
    pregunta: '¿Cómo se renuncia a una EPS?',
    respuesta:
      'Hay dos casos: 1. Dentro de los cinco días hábiles siguientes a la fecha de la publicación de los resultados de la elección de la EPS, los trabajadores, que así lo deseen, pueden manifestar al empleador su voluntad de no participar en el plan elegido y de mantener su cobertura de salud íntegramente a cargo de EsSalud; o, 2. Una vez Iniciado el plan de salud, los trabajadores individualmente pueden renunciar a la EPS y solicitar que EsSalud les brinde atención integral. Este derecho puede ser ejercido una vez dentro de cada año contractual.',
  },
  {
    pregunta: '¿Puedo cambiar de EPS?',
    respuesta:
      'Si eres un asegurado regular o dependiente, sólo podrás cambiar a la EPS que tu empleador escoja.',
  },
  {
    pregunta:
      'Si dejo de trabajar para el empleador que me afilió a una EPS, ¿puedo seguir pagando mi EPS para continuar con mi cobertura?',
    respuesta:
      'Sí, pero cambiando tu condición a la de un asegurado potestativo o independiente, teniendo que hacer la gestión dentro de los 60 días siguientes de haber dejado de laborar, para gozar de continuidad en tus tratamientos.',
  },
  {
    pregunta: '¿Se pierde cobertura si salgo de la empresa que me brinda la EPS?',
    respuesta:
      'Sí. Al dejar de trabajar para el empleador que te afilió a la EPS y no solicitas dentro del plazo mencionado en la pregunta anterior la continuidad como asegurado potestativo o independiente, pierdes el derecho de cobertura detallado en el plan de salud. Sin embargo, de acuerdo con los meses de aportación con que cuentes, podrás acceder a la cobertura de latencia solo en caso de desempleo o suspensión perfecta de labores.',
  },
  {
    pregunta: '¿Qué es el periodo de latencia?',
    respuesta:
      'Es el periodo que cuenta un asegurado regular o dependiente para poderse atender al estar en la calidad de desempleado o con una suspensión perfecta de labores. Para su goce, los afiliados regulares que cuenten con un mínimo de cinco meses de aportación, consecutivos o no consecutivos, durante los 3 años precedentes al cese o suspensión perfecta de labores, y sus derechohabientes, tienen el derecho a las prestaciones preventivas, promocionales y de recuperación de la salud, a razón de dos meses de latencia por cada cinco meses de aportación. Los períodos de latencia que se generen no podrán exceder de doce meses consecutivos, contados a partir de la fecha de cese o pérdida de la cobertura ocasionada por la suspensión perfecta de labores.',
  },
  {
    pregunta:
      '¿Cuál es el aporte y cuáles son los copagos en un plan regular o potestativo de una EPS?',
    respuesta:
      'Para los Asegurados Regulares o Dependientes: El aporte es asumido por el empleador, y equivale al 9% de la remuneración mensual del trabajador. Aquel porcentaje se disgrega de la siguiente forma: El 6.75% es pagado a EsSalud; el 2.25%, es pagado a la EPS. Los copagos son definidos por la EPS y son asumidos por el asegurado regular o dependiente. Para los Asegurados Potestativos o Independientes: El aporte es definido por la EPS y asumido por el asegurado potestativo o independiente. Los copagos son definidos por la EPS y son asumidos por el asegurado potestativo o independiente.',
  },
  {
    pregunta: '¿Qué es un reembolso?',
    respuesta:
      'Es una modalidad de atención que se acepta en caso te atiendas en una clínica o con un médico no afiliado a la red del plan de salud de la EPS. Si tu Plan incluye la modalidad de Reembolso, pagarás el 100% del costo de la atención y luego solicitarás a tu EPS que te reintegre el monto cubierto por tu plan de salud, previa aplicación del Deducible y Coaseguro respectivo. El Deducible y Coaseguro aplican en todos los casos de Reembolso.',
  },
];

const GLOSARIO: GlosarioItem[] = [
  {
    termino: 'AFILIADO',
    definicion:
      'Toda persona radicada en el país que esté bajo cobertura de salud de algunos de los regímenes del AUS.',
  },
  {
    termino: 'ARBITRAJE',
    definicion:
      'Fórmula que normalmente se introduce en los contratos de aseguramiento en virtud de la cual las diferencias que pudieran surgir entre el afiliado y la EPS respecto de la interpretación de las condiciones de la póliza se someten a la decisión de terceras personas designadas de mutuo acuerdo y en las que se presume una actuación imparcial y objetiva. Este procedimiento se utiliza para evitar procesos judiciales largos, de dudoso resultado por el contenido técnico específico del asunto juzgado y, por supuesto, muy costosos.',
  },
  {
    termino: 'CARTA DE GARANTÍA',
    definicion:
      'Documento emitido por EPS para el centro médico, en el que se certifica que los procedimientos indicados serán facturados a la EPS. Los procedimientos no contemplados en la carta de garantía serán pagados por el cliente. El criterio aplicado para ello se basa en las coberturas contratadas en la póliza o plan vigentes. En caso de necesitar una carta de garantía es el centro médico quién la tramita directamente con la compañía de seguros o EPS.',
  },
  {
    termino: 'CLÍNICAS',
    definicion:
      'Son los establecimientos donde se prestan los servicios de salud, ahora denominadas IPRESS.',
  },
  {
    termino: 'COBERTURAS',
    definicion:
      'Protección contra pérdidas específicas por problemas de salud, extendida bajo los términos del contrato.',
  },
  {
    termino: 'COPAGO',
    definicion:
      'El co-pago es la parte de los gastos que asume el afiliado. Puede ser fijo o variable (un porcentaje del costo).',
  },
  {
    termino: 'EMPLEADOR',
    definicion:
      'Persona, natural o jurídica, que contrata los servicios de salud para sus trabajadores y derechohabientes.',
  },
  {
    termino: 'URGENCIA',
    definicion:
      'Situación definida por un médico luego de comprobar que la condición de salud por la cual ingresó el asegurado o derechohabiente no representa un riesgo que atente contra la vida del asegurado.',
  },
  {
    termino: 'EMERGENCIA',
    definicion:
      'Situación definida por un médico luego de comprobar que la condición de salud por la cual ingresó el asegurado o derechohabiente representa un riesgo que atenta contra la vida del asegurado.',
  },
  {
    termino: 'ESSALUD',
    definicion:
      'El Seguro Social de Salud -EsSalud- es un organismo público descentralizado, con personería jurídica de derecho público interno, adscrito al Sector Trabajo y Promoción Social. Tiene por finalidad dar cobertura a los asegurados y sus derechohabientes, a través del otorgamiento de prestaciones de prevención, promoción, recuperación, rehabilitación, prestaciones económicas, y prestaciones sociales que corresponden al régimen contributivo de la Seguridad Social en Salud, así como otros seguros de riesgos humanos.',
  },
  {
    termino: 'EXCLUSIONES',
    definicion: 'Gastos o actividades que el seguro no cubre.',
  },
  {
    termino: 'GASTOS MÉDICOS',
    definicion:
      'Gastos de curación o de hospitalización, que incluyen los honorarios de los médicos y las medicinas.',
  },
  {
    termino: 'IPRESS',
    definicion:
      'Las Instituciones Prestadoras de Servicios de Salud -IPRESS- son aquellos establecimientos de salud y servicios médicos de apoyo, públicos, privados o mixtos, creados o por crearse, que realizan atención de salud con fines de prevención, promoción, diagnóstico, tratamiento y/o rehabilitación; así como aquellos servicios complementarios o auxiliares de la atención médica, que tienen por finalidad coadyuvar en la prevención, promoción, diagnóstico, tratamiento y/o rehabilitación de la salud.',
  },
  {
    termino: 'RENOVACIÓN',
    definicion:
      'Es el proceso que se efectúa entre el empleador y la EPS, con el objeto de renovar el plan de salud.',
  },
].sort((a, b) => a.termino.localeCompare(b.termino));

type Tab = 'preguntas' | 'glosario';

function FAQAccordionItem({
  item,
  index,
  isOpen,
  onToggle,
}: {
  item: FAQItem;
  index: number;
  isOpen: boolean;
  onToggle: () => void;
}): React.ReactElement {
  return (
    <div className="border-border/50 bg-card/80 shadow-strong overflow-hidden rounded-2xl border backdrop-blur-xl">
      <button
        onClick={onToggle}
        className="group hover:bg-card/60 flex w-full items-center justify-between px-6 py-5 text-left transition-all duration-300"
      >
        <div className="flex items-center space-x-4">
          <div className="from-secondary to-secondary/80 text-secondary-foreground shadow-medium flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-linear-to-r text-sm font-bold">
            {index + 1}
          </div>
          <h3 className="text-foreground group-hover:text-primary text-lg font-semibold transition-colors duration-300">
            {item.pregunta}
          </h3>
        </div>
        <ArrowRight
          className={cn(
            'text-muted-foreground h-5 w-5 shrink-0 transition-transform duration-300',
            isOpen && 'rotate-90'
          )}
        />
      </button>
      <div
        className={cn(
          'grid transition-all duration-300',
          isOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
        )}
      >
        <div className="overflow-hidden">
          <div className="px-6 pb-5">
            <div className="pl-12">
              <p className="text-muted-foreground leading-relaxed">{item.respuesta}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function GlosarioCard({ item }: { item: GlosarioItem }): React.ReactElement {
  return (
    <div className="group border-border/50 bg-card/80 shadow-strong hover:border-primary/30 hover:shadow-strong cursor-pointer rounded-2xl border p-6 backdrop-blur-xl transition-all duration-500 hover:-translate-y-2">
      <div className="flex items-start space-x-4">
        <div className="shrink-0">
          <div className="from-primary to-primary/80 text-primary-foreground shadow-medium flex h-12 w-12 items-center justify-center rounded-xl bg-linear-to-r text-lg font-bold">
            {item.termino.charAt(0)}
          </div>
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="text-foreground group-hover:text-primary mb-2 text-lg font-bold transition-colors duration-300">
            {item.termino}
          </h3>
          <p className="text-muted-foreground line-clamp-4 text-sm leading-relaxed transition-all duration-300 group-hover:line-clamp-none">
            {item.definicion}
          </p>
        </div>
      </div>
      <div className="mt-4 text-center opacity-0 transition-opacity duration-300 group-hover:opacity-100">
        <span className="bg-primary/10 text-primary inline-flex items-center rounded-full px-3 py-1 text-xs font-medium">
          <ArrowRight className="mr-1 h-3.5 w-3.5" />
          Ver completo
        </span>
      </div>
    </div>
  );
}

export function FAQPage(): React.ReactElement {
  useDocumentTitle('Preguntas Frecuentes');
  const [activeTab, setActiveTab] = useState<Tab>('preguntas');
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  function handleToggle(index: number): void {
    setOpenIndex(openIndex === index ? null : index);
  }

  return (
    <main className="from-background via-muted/30 to-primary/5 min-h-screen bg-linear-to-br pt-20">
      <div className="container mx-auto px-4 py-20">
        <div className="mb-16 text-center">
          <h1 className="mb-6 text-5xl font-bold md:text-6xl">
            <span className="from-primary to-secondary bg-linear-to-r bg-clip-text text-transparent">
              Preguntas Frecuentes
            </span>
          </h1>
          <p className="text-muted-foreground mx-auto max-w-3xl text-xl leading-relaxed">
            Encuentra respuestas a las dudas más comunes sobre EPS y seguros de salud
          </p>
        </div>

        <div className="mb-12 flex justify-center">
          <div className="border-border/50 bg-card/80 shadow-strong rounded-2xl border p-2 backdrop-blur-xl">
            <div className="flex space-x-2">
              <button
                onClick={() => setActiveTab('preguntas')}
                className={cn(
                  'flex items-center rounded-xl px-6 py-3 font-medium transition-all duration-300',
                  activeTab === 'preguntas'
                    ? 'from-primary to-primary/80 text-primary-foreground shadow-medium bg-linear-to-r'
                    : 'text-foreground hover:bg-card/60 hover:text-primary'
                )}
              >
                <Info className="mr-2 h-5 w-5" />
                Preguntas Frecuentes
              </button>
              <button
                onClick={() => setActiveTab('glosario')}
                className={cn(
                  'flex items-center rounded-xl px-6 py-3 font-medium transition-all duration-300',
                  activeTab === 'glosario'
                    ? 'from-primary to-primary/80 text-primary-foreground shadow-medium bg-linear-to-r'
                    : 'text-foreground hover:bg-card/60 hover:text-primary'
                )}
              >
                <FileText className="mr-2 h-5 w-5" />
                Glosario de Términos
              </button>
            </div>
          </div>
        </div>

        <div className="mx-auto max-w-6xl">
          {activeTab === 'preguntas' && (
            <div className="space-y-4">
              {PREGUNTAS_FRECUENTES.map((item, index) => (
                <FAQAccordionItem
                  key={index}
                  item={item}
                  index={index}
                  isOpen={openIndex === index}
                  onToggle={() => handleToggle(index)}
                />
              ))}
            </div>
          )}

          {activeTab === 'glosario' && (
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
              {GLOSARIO.map(item => (
                <GlosarioCard key={item.termino} item={item} />
              ))}
            </div>
          )}
        </div>

        <div className="mt-16 text-center">
          <div className="mx-auto max-w-2xl">
            <h3 className="text-foreground mb-6 text-2xl font-bold">
              ¿No encuentras lo que buscas?
            </h3>
            <p className="text-muted-foreground mb-8">
              Nuestro equipo está aquí para ayudarte con cualquier consulta específica
            </p>
            <div className="flex flex-col justify-center gap-4 sm:flex-row">
              <a
                href="mailto:soporte@apeps.org.pe?subject=Consulta%20de%20soporte%20-%20Portal%20APEPS"
                className="group from-primary to-primary/80 text-primary-foreground shadow-medium hover:shadow-strong focus:ring-primary inline-flex transform items-center rounded-xl bg-linear-to-r px-6 py-3 text-lg font-medium transition-all duration-300 hover:-translate-y-0.5 hover:scale-105 focus:ring-2 focus:ring-offset-2 focus:outline-none"
              >
                <Mail className="mr-2 h-5 w-5" />
                Contactar Soporte
              </a>
              <a
                href="/noticias"
                className="group text-foreground hover:border-primary/30 hover:bg-muted/50 hover:text-primary focus:ring-primary inline-flex transform items-center rounded-xl border-2 px-6 py-3 text-lg font-medium transition-all duration-300 hover:-translate-y-0.5 hover:scale-105 focus:ring-2 focus:ring-offset-2 focus:outline-none"
              >
                <FileText className="mr-2 h-5 w-5" />
                Ver Noticias
              </a>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
}
